import { useId, useMemo, useState } from 'react';
import {
  MEMBER_ROLES,
  MEMBER_ROLE_LABELS,
  type MemberPatch,
  type TenantMember,
  approvalGroupLabel,
} from '../../service/membership';

type Props = {
  member: TenantMember;
  isSelf: boolean;
  showApprovalGroups: boolean;
  /** Group names an admin can pick from; the member's own groups are added if missing. */
  approvalGroupOptions: string[];
  /** How many columns the table has, so the editor spans the row. */
  columnCount: number;
  busy: boolean;
  onSave(patch: MemberPatch): void;
  onRemove(): void;
  onCancel(): void;
};

const key = (group: string) => group.trim().toLowerCase();

/** Merges lists of group names, first spelling wins, case-insensitive. */
const mergeGroups = (...lists: string[][]): string[] => {
  const seen = new Set<string>();
  const merged: string[] = [];
  for (const list of lists) {
    for (const group of list) {
      const k = key(group);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      merged.push(group.trim());
    }
  }
  return merged;
};

const sameSet = (a: string[], b: string[]) => {
  const left = new Set(a.map(key));
  const right = new Set(b.map(key));
  return left.size === right.size && [...left].every((k) => right.has(k));
};

/**
 * The expanded panel under a member's row: role, approval groups (reward
 * only) and removal. Nothing is sent until Save or Confirm.
 */
export default function MemberEditor({
  member,
  isSelf,
  showApprovalGroups,
  approvalGroupOptions,
  columnCount,
  busy,
  onSave,
  onRemove,
  onCancel,
}: Props) {
  const id = useId();
  const [role, setRole] = useState(member.role);
  const [selected, setSelected] = useState<string[]>(member.approvalGroups);
  const [addedGroups, setAddedGroups] = useState<string[]>([]);
  const [newGroup, setNewGroup] = useState('');
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const options = useMemo(
    () => mergeGroups(approvalGroupOptions, member.approvalGroups, addedGroups),
    [approvalGroupOptions, member.approvalGroups, addedGroups],
  );
  const selectedKeys = new Set(selected.map(key));
  const roleChanged = role !== member.role;
  const groupsChanged = showApprovalGroups && !sameSet(selected, member.approvalGroups);
  const dirty = roleChanged || groupsChanged;

  const toggle = (group: string) => {
    setSelected((current) =>
      current.some((g) => key(g) === key(group))
        ? current.filter((g) => key(g) !== key(group))
        : [...current, group],
    );
  };

  const addGroup = () => {
    const group = newGroup.trim().replace(/\s+/g, ' ');
    if (!group) return;
    setAddedGroups((current) => mergeGroups(current, [group]));
    setSelected((current) => mergeGroups(current, [group]));
    setNewGroup('');
  };

  const save = () => {
    const patch: MemberPatch = {};
    if (roleChanged) patch.role = role;
    if (groupsChanged) patch.approvalGroups = options.filter((g) => selectedKeys.has(key(g)));
    onSave(patch);
  };

  return (
    <tr className="govuk-table__row">
      <td className="govuk-table__cell" colSpan={columnCount}>
        <div className="govuk-!-padding-4" style={{ backgroundColor: '#f3f2f1' }}>
          <h3 className="govuk-heading-s">Change {member.email}</h3>

          <div className="govuk-form-group">
            <fieldset className="govuk-fieldset">
              <legend className="govuk-fieldset__legend govuk-fieldset__legend--s">Role</legend>
              <div className="govuk-radios govuk-radios--inline govuk-radios--small">
                {MEMBER_ROLES.map((value) => (
                  <div className="govuk-radios__item" key={value}>
                    <input
                      className="govuk-radios__input"
                      id={`${id}-role-${value}`}
                      name={`${id}-role`}
                      type="radio"
                      value={value}
                      checked={role === value}
                      onChange={() => setRole(value)}
                      disabled={busy}
                    />
                    <label className="govuk-label govuk-radios__label" htmlFor={`${id}-role-${value}`}>
                      {MEMBER_ROLE_LABELS[value]}
                    </label>
                  </div>
                ))}
              </div>
            </fieldset>
          </div>

          {showApprovalGroups && (
            <div className="govuk-form-group">
              <fieldset className="govuk-fieldset">
                <legend className="govuk-fieldset__legend govuk-fieldset__legend--s">
                  Approval groups
                </legend>
                <div className="govuk-hint">
                  The functions whose nominations this person approves. Administrator covers
                  every function; COS approver takes the final approval stage.
                </div>
                <div className="govuk-checkboxes govuk-checkboxes--small">
                  {options.map((group, index) => (
                    <div className="govuk-checkboxes__item" key={group}>
                      <input
                        className="govuk-checkboxes__input"
                        id={`${id}-group-${index}`}
                        type="checkbox"
                        value={group}
                        checked={selectedKeys.has(key(group))}
                        onChange={() => toggle(group)}
                        disabled={busy}
                      />
                      <label
                        className="govuk-label govuk-checkboxes__label"
                        htmlFor={`${id}-group-${index}`}
                      >
                        {approvalGroupLabel(group)}
                      </label>
                    </div>
                  ))}
                </div>
                <div className="govuk-!-margin-top-3">
                  <label className="govuk-label" htmlFor={`${id}-new-group`}>
                    Add a group that is not listed
                  </label>
                  <input
                    className="govuk-input govuk-input--width-20"
                    id={`${id}-new-group`}
                    type="text"
                    value={newGroup}
                    onChange={(event) => setNewGroup(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        addGroup();
                      }
                    }}
                    disabled={busy}
                  />{' '}
                  <button
                    type="button"
                    className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                    onClick={addGroup}
                    disabled={busy || !newGroup.trim()}
                  >
                    Add group
                  </button>
                </div>
              </fieldset>
            </div>
          )}

          <div className="govuk-button-group govuk-!-margin-bottom-0">
            <button
              type="button"
              className="govuk-button govuk-!-margin-bottom-0"
              onClick={save}
              disabled={busy || !dirty}
            >
              Save
            </button>
            <button
              type="button"
              className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
              onClick={onCancel}
              disabled={busy}
            >
              Cancel
            </button>
            {isSelf ? (
              <span className="govuk-hint govuk-!-margin-0">You cannot remove yourself.</span>
            ) : confirmingRemove ? (
              <>
                <span className="govuk-body govuk-!-margin-0">Remove {member.email}?</span>
                <button
                  type="button"
                  className="govuk-button govuk-button--warning govuk-!-margin-bottom-0"
                  onClick={onRemove}
                  disabled={busy}
                >
                  Confirm
                </button>
                <button
                  type="button"
                  className="govuk-button govuk-button--secondary govuk-!-margin-bottom-0"
                  onClick={() => setConfirmingRemove(false)}
                  disabled={busy}
                >
                  Keep member
                </button>
              </>
            ) : (
              <button
                type="button"
                className="govuk-button govuk-button--warning govuk-!-margin-bottom-0"
                onClick={() => setConfirmingRemove(true)}
                disabled={busy}
              >
                Remove<span className="govuk-visually-hidden"> {member.email}</span>
              </button>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}
